import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import {
    borderWidthThin,
    fontWeightBold,
    spacing60,
    spacing80,
    spacing50,
    spacing40,
    spacing30,
    spacing20,
} from '@ellucian/react-design-system/core/styles/tokens';
import {
    Alert,
    INLINE_VARIANT,
    IconButton,
    Paper,
    Typography,
    Tooltip,
    Button,
    makeStyles,
    SimpleDialog,
    useMediaQuery,
} from '@ellucian/react-design-system/core';
import { useIntl, FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';
import ApplicationFormSideBar from './ApplicationFormSideBar';
import SidebarDialog from './SidebarDialog';
import ApplicationFormNavigationBar from './ApplicationFormNavigationBar';
import { Route, Redirect, Switch, useHistory, matchPath, generatePath, useParams } from 'react-router-dom';
import SubmittedDialog from './SubmittedDialog';
import ApplicationFormPage from './ApplicationFormPage';
import findIndex from 'lodash/findIndex';
import useApplicationForm from './useApplicationForm';
import SupplementalItemsChecklist from './SupplementalItemsChecklist';
import ApplicationPayments from './ApplicationPayments';
import { routes, applicantRoute, CONSTANTS } from '../Helpers/Configuration';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import moment from 'moment';
import InquiryNavigation from './Inquiry/InquiryNavigation/InquiryNavigation';
import InquiryConfirm from '../Inquiry/InquiryConfirm';
import classnames from 'classnames';
import WatchErrors from './redux/WatchErrors';
import { useDispatch, useSelector, batch } from 'react-redux';
import AutosaveStatus from './AutosaveStatus';
import Review from './Review';
import { checkForceShowErrors } from '@nsr/react-form';
import { goBackAction, goForwardAction, setPageAndCloseDialogAction, setPagesInitialStatusAction } from './redux/actions/navigationActions';
import { setSettingsValue } from './redux/slices/settingsSlice';
import isInIframe from '../../utilities/isInIframe';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice.js';
import ExperienceContext from '../../ExperienceContext';
import { setPageHeader } from '../../redux/admin/slices/adminBaseSlice';
import { setApplicationPageCompletedStatus, setApplication, setOriginalApplicationValues } from './redux/slices/applicationSlice';
import ApsSaveWarningDialog from './ApsSaveWarningDialog';
import { findChangedValues, getEmailPersonalizationQuestions, getApplicationValue, isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
import ApsSaveWarningTable from './ApsSaveWarningTable';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import forEach from 'lodash/forEach';
import {
    canEditApplication,
    canViewStagingRecord,
    canViewProcessingHistory,
    canSendEmailApplication
} from '../../utilities/actionCheck';
import ExecutionHistoryButton from '../Admin/TransferJobs/ExecutionHistoryButton';
import SendEmailDialog from '../Admin/Email/SendEmailDialog';
import BackArrow from '../Admin/AdminControls/BackArrow';
const { isApplicationFormTemplate } = require('@nsr/applicant-apply-shared/src/helpers/applicationTemplateHelper');

const useStyles = makeStyles(theme => ({
    '@global': {
        '.grecaptcha-badge': {
            opacity: 0,
        },
    },
    root: {
        margin: `3rem ${spacing80}`,
        paddingTop: spacing30,
        '&.inquiryRoot': {
            margin: '3rem auto 3rem auto',
            maxWidth: '800px',
        },
        '&.mobile': {
            marginTop: spacing30,
            paddingTop: 0,
        },
        '&.embedded': {
            marginTop: 0,
            paddingTop: 0,
        },
    },
    container: {
        marginTop: spacing30,
        display: 'grid',
        width: '100%',
    },
    iconRoot: {
        marginRight: spacing30,
    },
    [theme.breakpoints.up('md')]: {
        container: {
            gridTemplateAreas: `
                'sidebar content'
            `,
            gridTemplateColumns: 'max-content 3fr',
        },
    },
    content: {
        display: 'grid',
        gridArea: 'content',
        gridTemplateRows: 'max-content',
        padding: spacing50,
    },
    togglePanelButton: {
        margin: spacing40,
    },
    sidebar: {
        gridArea: 'sidebar',
        borderRight: `${borderWidthThin} solid rgba(0, 0, 0, 0.12)`,
        minHeight: '50vh',
    },
    bottomButtonContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    bottomButton: {
        paddingLeft: spacing50,
    },
    icon: {
        marginRight: spacing30
    },
    formTitle: {
        display: 'block',
    },
    formTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: spacing50,
        width:'100%'
    },
    returnButtonContainer: {
        marginRight: spacing40,
    },
    titleContainer: {},
    topBar: {
        display: 'flex'
    },
    readonlyText: {
        padding: spacing20,
    },
    saveBottonHeader: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: spacing30,
        justifyContent: 'flex-end',
        marginLeft: 'auto'
    },
    [theme.breakpoints.down('sm')]: {
        formTitleContainer: {
            display: 'block',
        },
        formTitle: {
            fontWeight: fontWeightBold,
        },
        container: {
            gridTemplateAreas: `
                'content'
            `,
            gridTemplateColumns: '1fr',
        },
        locale: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: spacing30
        },
        sidebar: {
            display: 'none',
            minHeight: 'unset',
        },
        root: {
            margin: `4rem ${spacing40} ${spacing60} ${spacing40}`,
        },
        titleContainer: {
            display: 'flex',
            '& > button': {
                marginRight: spacing40,
                marginTop: spacing30,
            },
        },
        bottomButton: {
            paddingLeft: 'spacing40'
        },
    },
}));

const showUseStyles = makeStyles(() => ({
    hide: {
        display: 'none',
    }
}));
const Show = ({ children, hide = false }) => {
    const classes = showUseStyles();
    return <div className={hide ? classes.hide : ''}>{children}</div>;
};

Show.propTypes = {
    children: PropTypes.node,
    hide: PropTypes.bool,
};
const selectApplication = state => state.application;
const selectOriginalApplicationValues = state => state.application.originalApplicationValues;
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectApplicationIsArchived = state => state.application?.archivedOn;
const selectApplicationIsWithdrawn = state => state.application?.status === CONSTANTS.applicationFormStatuses.WITHDRAWN;
const selectAllValues = state => state.form.values;
const selectFormErrors = state => state.form.errors;
const selectHiddenPageIds = state => state.settings.hiddenPageIds;
const selectNotifyTemplateUpdate = state => state.settings.notifyTemplateUpdate;
const selectApplicationIsPaymentPending = state => state.application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending;

const ApplicationForm = () => {
    const intl = useIntl();
    const snackbar = useSnackbar();
    const params = useParams();
    const { setExitPrompt } = useContext(ExperienceContext);
    const { localeDirection } = useContext(GlobalLocaleContext);
    const {
        externalMode,
        getApplicationFee,
        globalSettings,
        importAdminMode,
        inquiryMode,
        onLeavePreview,
        onViewStager,
        save,
        previewMode,
        supplementalFormMode,
        template = { pages: [] },
        hasPermission,
    } = useApplicationForm();
    const isAps = template?.settings?.applicationTemplateType === CONSTANTS.applicationTemplateTypes.import ? true : false;
    const dispatch = useDispatch();
    const history = useHistory();
    const { state: routeState } = history.location;
    const showDialog = useMemo(() => !!routeState?.showDialog, [routeState]);
    const routeMatch = matchPath(history.location.pathname, {
        path: [
            routes.adminTemplatePreviewPageById,
            routes.importsTemplatePreviewPageById,
            routes.applicationFormPageById,
            routes.externalApplicationPageById,
            routes.applicationFormSupplementalForm,
            routes.importsApplicationEditPageById
        ]
    });
    const isOnPayment = useMemo(() => {
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationFormPayments,
                routes.externalApplicationPayments,
            ]
        });
    }, [history.location]);

    const isOnReview = useMemo(() => {
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationFormReviewPage,
                routes.externalApplicationwReviewPage,
                routes.adminTemplatePreviewReviewPage,
                routes.importsTemplatePreviewReviewPage,
            ]
        });
    }, [history.location]);

    const application = useSelector(selectApplication);
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const isArchived = useSelector(selectApplicationIsArchived);
    const isWithdrawn = useSelector(selectApplicationIsWithdrawn);
    const hiddenPageIds = useSelector(selectHiddenPageIds);
    const notifyTemplateUpdate = useSelector(selectNotifyTemplateUpdate);
    const values = useSelector(selectAllValues);
    const originalValues = useSelector(selectOriginalApplicationValues);
    const formErrors = useSelector(selectFormErrors); 
    const isPaymentPending = useSelector(selectApplicationIsPaymentPending);
    const applicationStatus = application?.status;
    const [applicationFee, setApplicationFee] = useState();
    const [showSubmitted, setShowSubmitted] = useState(false);
    const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
    const [isSaveAdminApplicationOpen, setSaveAdminApplicationOpen] = useState(false);
    const [isEmailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailQuestions, setEmailQuestions] = useState('');
    const [changes, setChanges] = useState(null);
    const currentPageId = routeMatch?.params.pageId;
    const canViewTransferJobs = useMemo(() =>  !!hasPermission && canViewProcessingHistory(hasPermission), [hasPermission]);
    const canViewStagers = useMemo(() => !!hasPermission && canViewStagingRecord(hasPermission), [hasPermission]);
    const canSendEmail = useMemo(() => !!hasPermission && canSendEmailApplication(hasPermission), [hasPermission]);

    useEffect(() => {
        if (template?.templatePageTitle) {
            document.title = template.templatePageTitle;
        } else {
            document.title = globalSettings?.applyPageTitle;
        }
    }, [template?.templatePageTitle]);

    useEffect(() => {
        if (!isEqual(values, originalValues)) {
            setExitPrompt(true);
        }
        else setExitPrompt(false);
    }, [values, originalValues, setExitPrompt]);

    // Remove exit prompt on unmount
    useEffect(() => () => setExitPrompt(false), []);

    useEffect(() => {
        setExitPrompt(false);
    }, [history.location, setExitPrompt]);


    //Used to redirect from edit app page to read-only app if user cannot edit apps or app has certain statuses
    const canEditApp = useMemo(() => {
        return !!hasPermission && canEditApplication(hasPermission) &&
            ![CONSTANTS.applicationProcessingStatus.pending,
                CONSTANTS.applicationProcessingStatus.personCheck,
                CONSTANTS.applicationProcessingStatus.fraud].includes(applicationStatus);
    }, [hasPermission, applicationStatus]);

    useEffect(() => {
        if (importAdminMode) {
            dispatch(setPageHeader(intl.formatMessage({ id: 'adminApplicationHeader' })));
        }
    }, []);
    useEffect(() => {
        if ((isSubmitted || isArchived) && routeMatch && !supplementalFormMode) {
            let route = generatePath(routes.applicationFormSubmitPage, { id: application.id });
            if (previewMode && !isAps) {
                route = generatePath(routes.adminTemplatePreviewSubmitPage, { id: application.id });
            }
            if (previewMode && isAps) {
                route = generatePath(routes.importsTemplatePreviewSubmitPage, { id: application.id });
            }
            else if (externalMode) {
                route = generatePath(routes.externalApplicationSubmitPage, { id: application.id });
            }
            history.push(route);
        }
    }, [applicationStatus, externalMode, supplementalFormMode, isSubmitted, isArchived, routeMatch, previewMode, isAps]);

    useEffect(() => {
        if (isWithdrawn && routeMatch && !inquiryMode && !supplementalFormMode) {
            let route = routes.applicationFormWithdrawnPage.replace(':id', application.id);
            history.push(route);
        }
    }, [applicationStatus, inquiryMode, supplementalFormMode, isWithdrawn, routeMatch]);

    useEffect(() => {
        if (!supplementalFormMode && !isArchived) {
            if (template.allApplicationsRemovalAt && !isSubmitted) {
                const archivedDate = moment(template.allApplicationsRemovalAt).add(1, 'days').startOf('day');
                snackbar.show(<FormattedMessage id="applicationFormArchiveWarning" values={{ date: new Date(archivedDate).toLocaleString() }} />);
            }
            else if (applicationStatus === CONSTANTS.applicationFormStatuses.READONLY) {
                snackbar.show(<FormattedMessage id="applicationFormsHomeInactive" />);
            }
            else if (applicationStatus === CONSTANTS.applicationFormStatuses.WITHDRAWN) {
                snackbar.show(<FormattedMessage id="applicationFormsHomeWithdrawn" />);
            }
        }
    }, []);

    useEffect(() => {
        setShowSubmitted(isSubmitted && showDialog && !inquiryMode && !supplementalFormMode);
        setShowPaymentProcessing(!isSubmitted && showDialog && !inquiryMode && !supplementalFormMode && isOnReview);
        window.history.replaceState({ showDialog: null }, document.title);
    }, [isSubmitted, showDialog, inquiryMode, supplementalFormMode, isOnReview]);
    useEffect(() => {
        if (importAdminMode || notifyTemplateUpdate) {
            dispatch(setPagesInitialStatusAction());
        }
    }, [importAdminMode, dispatch]);

    useEffect(() => {
        const fn = async () => {
            try {
                const isOnPayment = matchPath(history.location.pathname, {
                    path: [
                        routes.applicationFormPayments,
                        routes.externalApplicationPayments,
                    ]
                });
                if (isOnPayment) {
                    dispatch(startLoading());
                    const result = await getApplicationFee(values);
                    setApplicationFee(result);
                    dispatch(stopLoading());
                }
            } catch (e) {
                console.error(e);
                dispatch(stopLoading());
            }
        };
        fn();
    }, [values, history?.location?.pathname, setApplicationFee, dispatch]);

    const classes = useStyles();
    const [hideSideBar, setHideSideBar] = useState(false);
    const toggleSidebarLabel = useMemo(() => intl.formatMessage({ id: hideSideBar ? 'expandPanel' : 'collapsePanel' }), [hideSideBar, intl]);
    const sidebarIcon = useMemo(() => {
        if ((hideSideBar && (!localeDirection || localeDirection === 'ltr')) ||
            (!hideSideBar && localeDirection === 'rtl')) {
            return 'arrow-double-right';
        }
        return 'arrow-double-left';
    }, [hideSideBar, localeDirection]);

    let page = null;
    if (routeMatch) {
        const pageIndex = findIndex(template.pages, { id: routeMatch.params.pageId });
        page = supplementalFormMode ? 0 : pageIndex;
    }
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const checkErrors = useCallback(errors => {
        dispatch(checkForceShowErrors({
            errors,
        }));
        // preserve force state errors, as it is completed in previous versions
        // now it should be stopped by validation
        if (notifyTemplateUpdate) {
            dispatch(setPagesInitialStatusAction());
        }
    }, [dispatch]);
    const goBackHome = useCallback(() => {
        history.push(routes.home);
    });
    const isEmbedded = useMemo(() => isInIframe(), []);
    const rootClasses = useMemo(() => {
        return classnames(classes.root, {
            inquiryRoot: inquiryMode,
            mobile: isMobile,
            embedded: isEmbedded,
        });
    }, [classes, inquiryMode, isEmbedded, isMobile, template?.type]);
    const onPageClick = useCallback((pageId) => {
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId,
            isAps
        }));
    }, [dispatch, history, isAps, formErrors]);

    const goBack = useCallback(previousPageId => {
        dispatch(goBackAction({ history, previousPageId, isAps }));
    }, [dispatch, history, isAps]);
    const goForward = useCallback(nextPageId => {
        dispatch(goForwardAction({ history, nextPageId, isAps }));
    }, [dispatch, history, isAps]);

    const showMobileSidebarDialog = useCallback(() => {
        dispatch(setSettingsValue({
            name: 'showMobileSidebarDialog',
            value: true,
        }));
    }, [dispatch]);

    const handleLeaveSupplementalForm = useCallback(() => {
        const route = routes.applicationFormSupplementalItemsPage.replace(':id', application.parentApplicationId);
        history.push(route);
    }, [history, application?.parentApplicationId]);    

    const closeEmailDialog = useCallback(() => {
        setEmailDialogOpen(false);
    }, []);

    const openEmailDialog = useCallback(() => {
        const fields = getEmailPersonalizationQuestions(template, CONSTANTS.applicationDefaultPersonalizationFields);
        const clonedValues = cloneDeep(values);
        forEach(fields, key => {
            let questionValue = getApplicationValue(clonedValues, key.questionId, key.dataId);
            if (questionValue) {
                key.value = questionValue[0];
            }
        });

        setEmailQuestions(fields);
        setEmailDialogOpen(true);
    }, [values, template]);
   
    const closeSaveAdminApplicationDialog = useCallback(() => {
        setSaveAdminApplicationOpen(false);
    }, []);

    const openSaveAdminApplicationDialog = useCallback(() => {
        if (!previewMode && importAdminMode) {
            const changedValueArray = findChangedValues(originalValues, values, template, intl.locale);
            setChanges(changedValueArray);
        }
        setSaveAdminApplicationOpen(true);
    }, [currentPageId, formErrors, application.pageStatuses, previewMode, importAdminMode, values, originalValues]);

    const handleSaveAdminApplication = useCallback(async (buttonId) => {
        setSaveAdminApplicationOpen(false);
        if (buttonId == 'save') {
            try {


                const newValues = values;
                let result = await save(newValues);

                if (result && result.status === CONSTANTS.applicationProcessingStatus.error) {
                    snackbar.error(<FormattedMessage id="adminApplicationSaveWithErrorsMessage" />);
                    batch(() => {
                        dispatch(setSettingsValue({
                            name: 'revision',
                            value: result.revision,
                        }));
                        dispatch(setApplication(result));
                        dispatch(setOriginalApplicationValues(cloneDeep(result.values)));

                    });
                }
                else {
                    snackbar.success(<FormattedMessage id="applicationProcessingSaveSuccess" />);
                    let route = generatePath(routes.importsApplicationViewPage, { id: application.id });
                    history.push(route);
                }
            }
            catch (e) {
                console.error(e);
                snackbar.error(<FormattedMessage id="adminApplicationUnableToSave" />);
            }
        }
        if (buttonId == 'cancel') {
            closeSaveAdminApplicationDialog();
        }

    }, [values, originalValues, setApplicationPageCompletedStatus, application, setSettingsValue, setApplication, setOriginalApplicationValues]);

    const handleSaveAdminApplicationFormSaveWarning = useCallback(async () => {
        try {

            const newValues = values;
            let result = await save(newValues);
            if (result && result.status === CONSTANTS.applicationProcessingStatus.error) {
                snackbar.error(<FormattedMessage id="adminApplicationSaveWithErrorsMessage" />);
            }
            else {
                snackbar.success(<FormattedMessage id="applicationProcessingSaveSuccess" />);
            }
        }
        catch (e) {
            console.error(e);
            snackbar.error(<FormattedMessage id="adminApplicationUnableToSave" />);
        }
    }, [values, originalValues, setApplicationPageCompletedStatus]);

    const containerClasses = useMemo(() => {
        return classnames(classes.container, {
            embedded: isEmbedded,
        });
    }, [classes, isEmbedded]);

    const closePaymentDialog = useCallback(() => setShowPaymentProcessing(false), [setShowPaymentProcessing]);

    const routeToApplicationManagement = useCallback(() => {
        let route = generatePath(routes.stagingRecords);
        history.push(route);
    }, [history]);

    return (
        <div className={rootClasses}>
            <ApsSaveWarningDialog handleSaveForm={handleSaveAdminApplicationFormSaveWarning} previewMode={previewMode || false} />
            {isMobile &&
                <>
                    {previewMode &&
                        <div className={classes.returnButtonContainer}>
                            <Button onClick={onLeavePreview}>
                                <Icon className={classes.iconRoot} name="chevron-left" />
                                <FormattedMessage id="leavePreview" />
                            </Button>
                        </div>
                    }
                    {supplementalFormMode &&
                        <div className={classes.returnButtonContainer}>
                            <Button onClick={handleLeaveSupplementalForm}>
                                <Icon className={classes.iconRoot} name="chevron-left" />
                                <FormattedMessage id="backToSupplementalItems" />
                            </Button>
                        </div>
                    }
                </>
            }
            <div className={classes.titleContainer}>
                <WatchErrors onChange={checkErrors} />
                {canSendEmail && emailQuestions &&
                <SendEmailDialog
                    isEmailDialogOpen={isEmailDialogOpen}
                    onClose={() => closeEmailDialog()}
                    personalization={emailQuestions}
                    fullScreen={isMobile}
                />}
                {isMobile &&
                    <>
                        {!inquiryMode && !supplementalFormMode &&
                            <Tooltip title={intl.formatMessage({ id: 'showApplicationFormSidebarDialog' })}>
                                <IconButton
                                    color="primary"
                                    aria-label={intl.formatMessage({ id: 'showApplicationFormSidebarDialog' })}
                                    onClick={showMobileSidebarDialog}
                                >
                                    <Icon name="menu" />
                                </IconButton>
                            </Tooltip>
                        }
                        <div className={classes.formTitleContainer}>
                            {importAdminMode &&
                                <Tooltip title={intl.formatMessage({ id: 'adminStagersReturn' })}>
                                    <IconButton onClick={routeToApplicationManagement} color="gray">
                                        <BackArrow />
                                    </IconButton>
                                </Tooltip>
                            }
                            {!inquiryMode &&
                                <Typography variant="h1" className={classes.formTitle}>{intl.formatMessage({ id:template.nameId })}</Typography>
                            }
                            {!isSubmitted && !isArchived && !previewMode && !externalMode && !inquiryMode && !isOnPayment && !importAdminMode &&
                                <AutosaveStatus />
                            }
                        </div>
                        {importAdminMode &&
                            <>
                                {!!application.transferJobs?.length && canViewTransferJobs && hasPermission && !externalMode && 
                                    <ExecutionHistoryButton id={params.id} locationState={'application'} hasPermission={hasPermission}/>
                                }
                                <div>
                                    {canViewStagers &&
                                        <IconButton onClick={onViewStager} color="gray">
                                            <Icon name="applications-alt" />
                                        </IconButton>
                                    }
                                    {canSendEmail &&
                                        <Tooltip title={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                            <IconButton onClick={() => openEmailDialog()} className={classes.icon} color="gray" value={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                <Icon name="email" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </div>
                                <SimpleDialog
                                    buttonConfig={[
                                        {
                                            id: 'save',
                                            title: <FormattedMessage id="confirm" />,
                                        },
                                        {
                                            id: 'cancel',
                                            title: <FormattedMessage id="actionCancel" />
                                        }
                                    ]}
                                    title={intl.formatMessage({ id: 'adminApplicationSaveTitle' })}
                                    open={isSaveAdminApplicationOpen}
                                    closeOnClickOut={false}
                                    handleButtonClick={handleSaveAdminApplication}
                                    showTitleBarClose={true}
                                    onClose={closeSaveAdminApplicationDialog}
                                    fullScreen={isMobile}
                                >
                                    <Typography><FormattedMessage id="adminApplicationSaveConfirmation" /></Typography>
                                    {!!changes?.length &&
                                        <ApsSaveWarningTable changes={changes} />
                                    }
                                </SimpleDialog>
                                <div className={classes.saveBottonHeader}>
                                    <Button onClick={() => openSaveAdminApplicationDialog()}>
                                        <FormattedMessage id="save" />
                                    </Button>
                                </div></>
                        }
                    </>
                }
                {!isMobile &&
                    <>
                        {supplementalFormMode &&
                            <div className={classes.returnButtonContainer}>
                                <Button disabled={previewMode} onClick={handleLeaveSupplementalForm}>
                                    <Icon className={classes.iconRoot} name="chevron-left" />
                                    <FormattedMessage id="backToSupplementalItems" />
                                </Button>
                            </div>
                        }
                        <div className={classes.topBar}>
                            <div className={classes.formTitleContainer}>
                                {importAdminMode &&
                                    <Tooltip title={intl.formatMessage({ id: 'adminStagersReturn' })}>
                                        <IconButton onClick={routeToApplicationManagement} color="gray">
                                            <BackArrow />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {previewMode &&
                                    <div className={classes.returnButtonContainer}>
                                        <Button onClick={onLeavePreview}>
                                            <Icon className={classes.iconRoot} name="chevron-left" />
                                            <FormattedMessage id="leavePreview" />
                                        </Button>
                                    </div>
                                }
                                {!inquiryMode &&
                                    <Typography variant="h1" className={classes.formTitle}>
                                        {intl.formatMessage({ id:template.nameId })}
                                    </Typography> 
                                }
                                {!isSubmitted && !isArchived && !previewMode && !externalMode && !inquiryMode && !isOnPayment && !importAdminMode &&
                                    <AutosaveStatus />
                                }
                            </div>
                            {importAdminMode &&
                                <>

                                    <SimpleDialog
                                        buttonConfig={[
                                            {
                                                id: 'save',
                                                title: <FormattedMessage id="confirm" />,
                                            },
                                            {
                                                id: 'cancel',
                                                title: <FormattedMessage id="actionCancel" />
                                            }
                                        ]}
                                        title={intl.formatMessage({ id: 'adminApplicationSaveTitle' })}
                                        open={isSaveAdminApplicationOpen}
                                        closeOnClickOut={false}
                                        handleButtonClick={handleSaveAdminApplication}
                                        showTitleBarClose={true}
                                        onClose={closeSaveAdminApplicationDialog}
                                        fullScreen={isMobile}
                                        maxWidth="lg"
                                        fullWidth
                                    >
                                        <Typography><FormattedMessage id="adminApplicationSaveConfirmation" /></Typography>
                                        {!!changes?.length &&
                                            <ApsSaveWarningTable changes={changes} />
                                        }
                                    </SimpleDialog>                                   
                                    <div className={classes.saveBottonHeader}>
                                        {!!application.transferJobs?.length && canViewTransferJobs && hasPermission && !externalMode && 
                                            <ExecutionHistoryButton id={params.id} locationState={'application'} hasPermission={hasPermission}/>
                                        }
                                        {canViewStagers &&
                                            <Tooltip title={intl.formatMessage({ id: 'adminApplicationViewStager' })}>
                                                <IconButton onClick={onViewStager} color="gray" value={intl.formatMessage({ id: 'adminApplicationViewStager' })}>
                                                    <Icon name="applications-alt" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {canSendEmail &&
                                            <Tooltip title={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                <IconButton onClick={() => openEmailDialog()} className={classes.icon} color="gray" value={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                    <Icon name="email" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        <Button onClick={() => openSaveAdminApplicationDialog()}>
                                            <FormattedMessage id="save" />
                                        </Button>
                                    </div></>
                            }
                        </div>
                    </>
                }
            </div>
            {application?.archivedOn &&
                <div>
                    <Alert
                        alertType='info'
                        open={true}
                        userDismissable={false}
                        text={<FormattedMessage id="applicationFormArchivedAlert" values={{ date: new Date(application.archivedOn).toLocaleString() }} />}
                        variant={INLINE_VARIANT}
                    />
                </div>
            }
            { applicationStatus === CONSTANTS.applicationFormStatuses.STARTED && notifyTemplateUpdate &&
                    <Alert
                        alertType="info"
                        open={true}
                        userDismissable={false}
                        variant={INLINE_VARIANT}
                        text={<FormattedMessage id="updatedApplicationFormTextId" />}
                    />
            }
            {applicationStatus === CONSTANTS.applicationFormStatuses.READONLY &&
                <>
                    <Typography className={classes.readonlyText}>{intl.formatMessage({ id: 'reviewInactiveForm' })}</Typography>
                    <Button variant="text" onClick={goBackHome}>{intl.formatMessage({ id: 'applicationFormClickHereToGoBack' })}</Button>
                </>
            }
            {applicationStatus === CONSTANTS.applicationFormStatuses.WITHDRAWN &&
                <>
                    <Typography className={classes.readonlyText}>{intl.formatMessage({ id: 'applicationFormWithdrawnWarning' })}</Typography>
                </>
            }
            {applicationStatus !== CONSTANTS.applicationFormStatuses.READONLY &&
                <Paper className={containerClasses}>
                    { isApplicationFormTemplate(template.templateType) &&
                        <div className={classes.sidebar}>
                            <Tooltip title={toggleSidebarLabel}>
                                <IconButton
                                    color="secondary"
                                    aria-label={toggleSidebarLabel}
                                    className={classes.togglePanelButton}
                                    onClick={() => setHideSideBar(!hideSideBar)}
                                >
                                    <Icon name={sidebarIcon} />
                                </IconButton>
                            </Tooltip>
                            {!isMobile &&
                                <Show hide={hideSideBar}>
                                    <ApplicationFormSideBar
                                        page={template.pages[page]}
                                        onPageClick={onPageClick}
                                    />
                                </Show>
                            }
                            {isMobile &&
                                <SidebarDialog>
                                    <ApplicationFormSideBar
                                        page={template.pages[page]}
                                        onPageClick={onPageClick}
                                    />
                                </SidebarDialog>
                            }
                        </div>
                    }
                    {showSubmitted && <SubmittedDialog isOpen={showSubmitted} onClose={() => setShowSubmitted(false)} />}
                    {showPaymentProcessing &&
                        <SimpleDialog
                            buttonConfig={[
                                {
                                    id: 'ok',
                                    title: intl.formatMessage({ id: 'dialogOk' })
                                },
                            ]}
                            title={intl.formatMessage({ id: 'payProcessingHeader' })}
                            open={showPaymentProcessing}
                            closeOnClickOut={false}
                            handleButtonClick={closePaymentDialog}
                            showTitleBarClose={true}
                            onClose={closePaymentDialog}
                            fullScreen={isMobile}
                        >
                            <Typography><FormattedMessage id="payProcessingContent" /></Typography>
                        </SimpleDialog>
                    }
                    <div className={classes.content}>
                        <Switch>
                            {!canEditApp && <Route exact path={[routes.importsApplicationEditPage, routes.importsApplicationEditPageById]}>
                                <Redirect to={generatePath(routes.importsApplicationViewPage, { id: application.id })} />
                            </Route>
                            }
                            <Redirect
                                exact
                                from={routes.applicationFormStart}
                                to={isSubmitted
                                    ? routes.applicationFormSubmitPage
                                    : isPaymentPending
                                        ? applicantRoute(routes.applicationFormReviewPage, application.id) 
                                        : applicantRoute(routes.applicationFormPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.applicationFormStart}
                                to={isWithdrawn
                                    ? routes.applicationFormWithdrawnPage
                                    : applicantRoute(routes.applicationFormPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.adminTemplatePreview}
                                to={applicantRoute(routes.adminTemplatePreviewPageById, template.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.importsTemplatePreview}
                                to={applicantRoute(routes.importsTemplatePreviewPageById, template.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.importsApplicationEditPage}
                                to={applicantRoute(routes.importsApplicationEditPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.externalApplication}
                                to={applicantRoute(routes.externalApplicationPageById, application.id, template.pages[0].id)}
                            />
                            <Route exact path={[
                                routes.adminTemplatePreviewSubmitPage,
                                routes.importsTemplatePreviewSubmitPage,
                                routes.applicationFormSubmitPage,
                                routes.externalApplicationSubmitPage,
                                routes.applicationFormWithdrawnPage
                            ]}>
                                {!inquiryMode &&
                                    <Review onPageClick={onPageClick} />
                                }
                                {inquiryMode &&
                                    <InquiryConfirm commType={CONSTANTS.communicationTypes.email} />
                                }
                            </Route>
                            {template.settings.enableSupplementalItems && template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0 &&
                                <Route exact path={[
                                    routes.adminTemplatePreviewSupplementalItemsPage,
                                    routes.applicationFormSupplementalItemsPage,
                                    routes.externalApplicationSupplementalItemsPage,
                                ]}>
                                    <SupplementalItemsChecklist />
                                </Route>
                            }
                            <Route path={[
                                routes.applicationFormPageById,
                                routes.adminTemplatePreviewPageById,
                                routes.externalApplicationPageById,
                                routes.applicationFormSupplementalForm,
                                routes.importsTemplatePreviewPageById,
                                routes.importsApplicationEditPageById
                            ]}>
                                <>
                                    {inquiryMode && !isEmbedded &&
                                        <InquiryNavigation
                                            onBack={goBack}
                                            onContinue={goForward}
                                        />
                                    }
                                    {inquiryMode && !previewMode &&
                                        <div className='g-recaptcha' />
                                    }
                                    {template.pages[page] && (!isSubmitted && !isPaymentPending) && (!hiddenPageIds || !hiddenPageIds.includes(routeMatch.params.pageId)) &&
                                        <ApplicationFormPage page={template.pages[page]} />
                                    }
                                    {supplementalFormMode && (isSubmitted || isPaymentPending) &&
                                        <Review onPageClick={onPageClick} />
                                    }
                                </>
                            </Route>
                            <Route path={[
                                routes.applicationFormReviewPage,
                                routes.adminTemplatePreviewReviewPage,
                                routes.externalApplicationwReviewPage,
                                routes.importsTemplatePreviewReviewPage,
                            ]}>
                                <Review onPageClick={onPageClick} />
                            </Route>
                            <Route path={[
                                routes.applicationFormPayments,
                                routes.externalApplicationPayments,
                            ]}>
                                <ApplicationPayments applicationFee={applicationFee} />
                            </Route>
                        </Switch>
                    </div>
                </Paper>
            }

            <ApplicationFormNavigationBar
                onBack={goBack}
                onContinue={goForward}
                onPageClick={onPageClick}
            />
        </div>
    );
};

export default ApplicationForm;
